import {
  CurrencyUtils,
  EmptyState,
  SimpleCard,
  useIsMobile,
} from '@brunommsilva/react-foundations';
import {
  Box,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CATEGORY_ICON_MAP } from '../../config/category-icons';
import { CashflowCategory } from '../../models/cashflows/cashflow';
import { HorizontalChart } from '../icons';

const CHART_COLORS = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#E7E9ED',
  '#8A2BE2',
  '#00FA9A',
  '#DC143C',
];

// Each category is 42px tall, with 20px between them, and the list has 8px of room above and below.
const CATEGORY_HEIGHT = 42;
const CATEGORY_GAP = 20;
const LIST_PADDING = 16;

interface Props {
  categories?: CashflowCategory[];
  title?: string;
  type: 'expenses' | 'revenue';
  isLoading?: boolean;
  onCategoryClick?: (category: CashflowCategory) => void;
}

const CategoryBreakdownChart = ({
  categories,
  title,
  type,
  isLoading,
  onCategoryClick,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // The list shows this many categories at a time, and the rest is a scroll; the skeleton and the empty
  // state keep the same height.
  const visibleCategories = isMobile ? 3 : 5;
  const listHeight =
    visibleCategories * CATEGORY_HEIGHT +
    (visibleCategories - 1) * CATEGORY_GAP +
    LIST_PADDING;

  const renderChart = () => {
    if (isLoading) {
      return (
        <Box sx={{ height: listHeight, overflowY: 'hidden', pr: 1.5 }}>
          <Stack spacing={2.5} sx={{ pt: 1, pb: 1 }}>
            {Array.from({ length: visibleCategories }).map((_, index) => (
              <Box key={`skeleton-cat-${index}`}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    mb: 0.75,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="circular" width={20} height={20} />
                    <Skeleton variant="text" width={120} height={20} />
                  </Box>
                  <Skeleton variant="text" width={80} height={20} />
                </Box>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={8}
                  sx={{ borderRadius: 4 }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      );
    }
    if (!categories || categories.length === 0) {
      return (
        <Box
          sx={{
            height: listHeight,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('NO_DATA')}
          </Typography>
        </Box>
      );
    }

    const sortedCategories = [...categories]
      .filter((cat) => cat[type] > 0)
      .sort((a, b) => b[type] - a[type])
      .slice(0, 10);

    if (sortedCategories.length === 0) {
      return (
        <EmptyState
          title={t('NO_DATA')}
          Icon={HorizontalChart}
          height={listHeight}
        />
      );
    }

    const totalAmount = categories.reduce((acc, cat) => acc + cat[type], 0);
    const maxAmount = sortedCategories[0][type];

    return (
      <Box
        sx={{
          height: listHeight,
          overflowY: 'auto',
          pr: 1.5,
          // The list stops on a whole category, and never in the middle of one.
          scrollSnapType: 'y mandatory',
          scrollPaddingTop: '8px',
        }}
      >
        <Stack spacing={2.5} sx={{ pt: 1, pb: 1 }}>
          {sortedCategories.map((category, index) => {
            const amount = category[type];
            const percentage = (amount / maxAmount) * 100;
            const truePercentage =
              totalAmount > 0
                ? ((amount / totalAmount) * 100).toFixed(1)
                : '0.0';
            const color = CHART_COLORS[index % CHART_COLORS.length];

            const IconComponent = category.icon
              ? CATEGORY_ICON_MAP[category.icon]
              : null;

            return (
              <Box
                key={category.name}
                onClick={() => onCategoryClick?.(category)}
                sx={{
                  scrollSnapAlign: 'start',
                  cursor: onCategoryClick ? 'pointer' : 'default',
                  borderRadius: 1,
                  p: 0.5,
                  mx: -0.5,
                  transition: 'background-color 0.2s, transform 0.1s',
                  '&:hover': {
                    bgcolor: onCategoryClick ? 'action.hover' : 'transparent',
                    transform: onCategoryClick ? 'translateX(4px)' : 'none',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    mb: 0.75,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {IconComponent ? (
                      <IconComponent
                        sx={{
                          fontSize: 18,
                          color: color,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: color,
                        }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: '600',
                      }}
                    >
                      {category.name}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                    }}
                  >
                    {CurrencyUtils.formatWithCurrency(amount, 0)}{' '}
                    <Box
                      component="span"
                      sx={{ color: 'text.secondary', ml: 0.5, fontWeight: 500 }}
                    >
                      ({truePercentage}%)
                    </Box>
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={percentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: `${color}20`,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: color,
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  };

  return <SimpleCard title={title || ''} content={renderChart()} />;
};

export default CategoryBreakdownChart;
